/*
 * BS.Web.Blog: infinite scroll for a paged overview. The TypeScript source of the static web asset
 * _content/BS.Web.Blog/bs-infinite.js (wwwroot/bs-infinite.js, compiled on build to ES2019 with a source map):
 *
 *   <script src="/_content/BS.Web.Blog/bs-infinite.js" defer></script>
 *
 * The overview is paged on the server (BlogIndexPage): /guides, /guides?page=2, ... Every page is complete without JavaScript
 * and links the next one, so crawlers and readers without the script page through the links. When the reader comes within
 * 800px of that link, the script loads the next page, appends the cards of its list to this page's list and points the link at
 * the page after it; after the last page it removes the link. The address in the address bar stays as it is, and keyboard
 * focus stays where it is.
 *
 *   <ul data-bs-list> <li>card</li> <li>card</li> ... </ul>     the list: any element, its children are the cards
 *   <a data-bs-more href="/guides?page=2">Load more</a>         the link to the next page: outside the list, visible
 *
 * The next page carries the same two markers: the children of its list are appended, the href of its link becomes the link's
 * (no link there: that was the last page). A list pairs with the link of the same name, data-bs-list="briefings" with
 * data-bs-more="briefings", an unnamed list with an unnamed link; so a page may hold more than one paged list.
 *
 * Nothing happens without IntersectionObserver, fetch and DOMParser. Loading stops at the first thing that is not as expected
 * (a network error, a status other than 2xx, a page without the list, a link to another site or to a page already loaded),
 * and the link stays what it is without the script: an ordinary link to the next page. While a page loads, the list carries
 * aria-busy="true". No dependencies, no inline code and no eval: it runs under a strict Content-Security-Policy
 * (script-src 'self').
 *
 * One job per part (SOLID): SiteAddress decides which addresses may be loaded and how a loaded page's links resolve; Markers
 * finds a list and its link in a document; PageFetcher loads and parses a page (IPageSource); CardList takes the cards and
 * shows the loading state (ICardList); MoreLink points the link on or removes it (INextLink); NearTrigger watches the link
 * with an IntersectionObserver (ITrigger). InfiniteList knows only these contracts and the order of things, and start() wires
 * one InfiniteList per list. All of it lives in one function scope: nothing is left in the global scope.
 */
(() => {
  /** The list's attribute; its value names the pair ('' for none). */
  const LIST = "data-bs-list";

  /** The link's attribute, with the same value as its list's. */
  const MORE = "data-bs-more";

  /** How far ahead of the reader the next page loads: 800px above and below the viewport. */
  const AHEAD = "800px 0px";

  /** Marks a list that is watched, so a second copy of the script on the page leaves it alone. */
  const WATCHED = Symbol.for("bs-web-blog-infinite");

  /** A page of the site as it was loaded: its address after redirects and its parsed document. */
  interface LoadedPage {
    readonly address: string;
    readonly document: Document;
  }

  /** Loads a page of the site; rejects when it is no page to show (a network error, a status other than 2xx, another site). */
  interface IPageSource {
    load(address: string): Promise<LoadedPage>;
  }

  /** The page's list. */
  interface ICardList {
    /** Shows that the next page loads (aria-busy), or that it no longer does. */
    busy(loading: boolean): void;

    /** Moves the children of a loaded page's list to the end of this one. */
    take(source: Element): void;
  }

  /** The link to the next page. */
  interface INextLink {
    /** The page of this site the link points at, or null (no href, another site). */
    target(): string | null;

    /** Points the link at the page after the one just loaded. */
    pointAt(address: string): void;

    /** Takes the link away after the last page. */
    remove(): void;
  }

  /** Calls back when the reader comes near the link. */
  interface ITrigger {
    watch(near: () => void): void;

    /** Looks again after a page was appended: the link may still be near. */
    rearm(): void;

    stop(): void;
  }

  /** Which addresses may be loaded (pages of this site over http or https), and how the links of a loaded page resolve. */
  class SiteAddress {
    public constructor(private readonly origin: string) {}

    /** The address resolved against base, without its fragment; null unless it is a page of this site. */
    public page(href: string, base: string): string | null {
      try {
        const url = new URL(href, base);
        url.hash = "";
        return url.origin === this.origin && (url.protocol === "https:" || url.protocol === "http:") ? url.href : null;
      } catch {
        return null;
      }
    }

    /** An href of a loaded page as the browser would follow it there: against its <base href>, else against its own address. */
    public resolve(page: LoadedPage, href: string): string {
      try {
        const base = page.document.querySelector("base[href]");
        const root = base === null ? page.address : new URL(base.getAttribute("href") || "", page.address).href;
        return new URL(href, root).href;
      } catch {
        return href;
      }
    }
  }

  /** The markup contract: the list and the link of one name in a document. */
  class Markers {
    public constructor(private readonly name: string) {}

    public list(root: ParentNode): Element | null {
      return this.named(root, LIST);
    }

    public link(root: ParentNode): Element | null {
      return this.named(root, MORE);
    }

    // The first element whose attribute has the pair's name as its value ('' for the attribute without a value).
    private named(root: ParentNode, attribute: string): Element | null {
      const elements = root.querySelectorAll(`[${attribute}]`);
      for (let i = 0; i < elements.length; i++) {
        if (elements[i].getAttribute(attribute) === this.name) {
          return elements[i];
        }
      }
      return null;
    }
  }

  /** Loads a page of the site with fetch and parses it with DOMParser, which leaves its scripts inert. */
  class PageFetcher implements IPageSource {
    public constructor(private readonly addresses: SiteAddress) {}

    public async load(address: string): Promise<LoadedPage> {
      const response = await fetch(address, { headers: { Accept: "text/html" } });
      const final = this.addresses.page(response.url || address, address);
      if (!response.ok || final === null) {
        throw new Error(`${address} answered ${response.status}`);
      }
      return { address: final, document: new DOMParser().parseFromString(await response.text(), "text/html") };
    }
  }

  /** The page's list: takes over the cards of a loaded list and carries aria-busy="true" while a page loads. */
  class CardList implements ICardList {
    public constructor(private readonly element: Element) {}

    public busy(loading: boolean): void {
      if (loading) {
        this.element.setAttribute("aria-busy", "true");
      } else {
        this.element.removeAttribute("aria-busy");
      }
    }

    public take(source: Element): void {
      const cards = this.element.ownerDocument.createDocumentFragment();
      while (source.firstChild !== null) {
        cards.appendChild(source.firstChild);
      }
      this.element.appendChild(cards);
    }
  }

  /** The link to the next page: where it points, pointed on, removed after the last page. */
  class MoreLink implements INextLink {
    public constructor(
      private readonly element: Element,
      private readonly addresses: SiteAddress,
    ) {}

    public target(): string | null {
      const href = this.element.getAttribute("href");
      return href ? this.addresses.page(href, this.element.baseURI) : null;
    }

    public pointAt(address: string): void {
      this.element.setAttribute("href", address);
    }

    public remove(): void {
      this.element.remove();
    }
  }

  /** Calls back when an element comes within a margin around the viewport, through an IntersectionObserver. */
  class NearTrigger implements ITrigger {
    private observer: IntersectionObserver | null = null;

    public constructor(
      private readonly element: Element,
      private readonly margin: string,
    ) {}

    public watch(near: () => void): void {
      this.stop();
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.length > 0 && entries[entries.length - 1].isIntersecting) {
            near();
          }
        },
        { rootMargin: this.margin },
      );
      this.observer.observe(this.element);
    }

    // An observer reports changes only; observed anew, it reports at once whether the element is still near.
    public rearm(): void {
      if (this.observer !== null) {
        this.observer.unobserve(this.element);
        this.observer.observe(this.element);
      }
    }

    public stop(): void {
      if (this.observer !== null) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  /**
   * One paged list: when the trigger fires it loads the page the link points at, hands that page's cards to the list and moves
   * the link on, or removes it after the last page. One page at a time and each page once; whatever is not as expected stops
   * it and leaves the link an ordinary link.
   */
  class InfiniteList {
    private readonly loaded = new Set<string>();
    private loading = false;

    public constructor(
      private readonly markers: Markers,
      private readonly list: ICardList,
      private readonly link: INextLink,
      private readonly trigger: ITrigger,
      private readonly pages: IPageSource,
      private readonly addresses: SiteAddress,
      shown: string | null,
    ) {
      if (shown !== null) {
        this.loaded.add(shown);
      }
    }

    public start(): void {
      this.trigger.watch(() => this.next());
    }

    private next(): void {
      if (this.loading) {
        return;
      }
      const address = this.link.target();
      if (address === null || this.loaded.has(address)) {
        this.trigger.stop();
        return;
      }
      this.loading = true;
      this.loaded.add(address);
      this.list.busy(true);
      void this.pages
        .load(address)
        .then((page) => this.show(address, page))
        .catch(() => this.fail());
    }

    private show(address: string, page: LoadedPage): void {
      const cards = this.markers.list(page.document);
      // A page without the list, or a redirect to a page that is shown already (e.g. back to the first), is not the next one.
      if (cards === null || (page.address !== address && this.loaded.has(page.address))) {
        this.fail();
        return;
      }
      this.loaded.add(page.address);
      const more = this.markers.link(page.document);
      const href = more === null ? null : more.getAttribute("href");
      this.list.take(cards);
      this.done();
      if (href) {
        this.link.pointAt(this.addresses.resolve(page, href));
        this.trigger.rearm();
      } else {
        this.trigger.stop();
        this.link.remove();
      }
    }

    // Not as expected: no more loading, and the link stays an ordinary link to the next page.
    private fail(): void {
      this.done();
      this.trigger.stop();
    }

    private done(): void {
      this.loading = false;
      this.list.busy(false);
    }
  }

  /** Wires one InfiniteList for every list on the page whose link stands outside it; a list is wired once. */
  function start(): void {
    const addresses = new SiteAddress(window.location.origin);
    const pages = new PageFetcher(addresses);
    const shown = addresses.page(window.location.href, window.location.href);
    const lists = document.querySelectorAll(`[${LIST}]`);
    for (let i = 0; i < lists.length; i++) {
      const element = lists[i];
      const markers = new Markers(element.getAttribute(LIST) || "");
      const link = markers.link(document);
      if (WATCHED in element || link === null || element.contains(link)) {
        continue;
      }
      Object.defineProperty(element, WATCHED, { value: true });
      new InfiniteList(
        markers,
        new CardList(element),
        new MoreLink(link, addresses),
        new NearTrigger(link, AHEAD),
        pages,
        addresses,
        shown,
      ).start();
    }
  }

  if (
    typeof window.IntersectionObserver === "function" &&
    typeof window.fetch === "function" &&
    typeof window.DOMParser === "function" &&
    typeof window.URL === "function"
  ) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", start);
    } else {
      start();
    }
  }
})();
